import React, { useEffect, useState } from "react";
import { getCreditAnalysisAdditionalData } from "../../../../shared/services/apis/tenantAnalytics";
import IframeViewer from "../../../../shared/ui/urlOpener/IFrameViewer";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  Packer,
  convertInchesToTwip,
  TableLayoutType,
  PageOrientation,
  Footer,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import { RiDownloadLine } from "react-icons/ri";
import { getTenantById } from "../../../../shared/services/apis";

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

interface AdditionalDataProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export const AdditionalData: React.FC<AdditionalDataProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };
    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getCreditAnalysisAdditionalData(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
        setError(null);
      } catch {
        setError("Failed to load additional data");
      } finally {
        setLoading(false);
      }
    };
    if (tenantId && tenantServiceId && tenantServiceOptionId) fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const formatValue = (value: any): string => {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === "object") {
            return Object.entries(item)
              .map(([k, v]) => `${k}: ${v}`)
              .join(", ");
          }
          return item;
        })
        .join("; ");
    }
    if (typeof value === "object" && value !== null) {
      return Object.entries(value)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
    }
    return String(value);
  };

  const downloadPDF = async () => {
    setDownloading(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const sections = data?.content?.sections;

      // Load the watermark image with proper typing
      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      // Load watermark image if logoUrl exists
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      // Function to add watermark to a page
      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;

        doc.setPage(pageNumber);

        // Save graphics state and set transparency
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 })); // Remove 'new' keyword

        // Calculate watermark size and position (centered)
        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        // Add watermark image
        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );

        // Restore graphics state
        doc.restoreGraphicsState();
      };

      // Header
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Additional Data", pageWidth / 2, 20, { align: "center" });

      // Date and time
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 28, {
        align: "center",
      });

      let yPosition = 40;

      // Sections
      sections?.forEach((section: any) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 60) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(section.section, 14, yPosition);
        yPosition += 8;

        if (section.details) {
          const tableData = Object.entries(section.details)
            .filter(([_, value]) => !Array.isArray(value) || value.length === 0)
            .map(([key, value]) => [
              key
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l: string) => l.toUpperCase()),
              formatValue(value),
            ]);

          if (tableData.length > 0) {
            autoTable(doc, {
              startY: yPosition,
              head: [["Field", "Details"]],
              body: tableData,
              theme: "grid",
              styles: { fontSize: 8, cellPadding: 2 },
              headStyles: {
                fillColor: [243, 244, 246],
                textColor: [55, 65, 81],
                fontStyle: "bold",
              },
              columnStyles: {
                0: { cellWidth: 50 },
                1: { cellWidth: "auto" },
              },
              margin: { left: 14, right: 14 },
            });

            yPosition = (doc as any).lastAutoTable.finalY + 10;
          }

          // Handle array fields separately
          Object.entries(section.details).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              if (yPosition > pageHeight - 40) {
                doc.addPage();
                yPosition = 20;
              }

              doc.setFontSize(10);
              doc.setFont("helvetica", "bold");
              doc.text(
                key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l: string) => l.toUpperCase()),
                14,
                yPosition
              );
              yPosition += 6;

              value.forEach((item: any) => {
                if (yPosition > pageHeight - 20) {
                  doc.addPage();
                  yPosition = 20;
                }

                doc.setFontSize(8);
                doc.setFont("helvetica", "normal");
                const itemText =
                  typeof item === "object"
                    ? Object.entries(item)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(", ")
                    : item;

                const splitText = doc.splitTextToSize(itemText, pageWidth - 28);
                doc.text(splitText, 18, yPosition);
                yPosition += splitText.length * 4 + 3;
              });

              yPosition += 5;
            }
          });
        }

        yPosition += 5;
      });

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        addWatermark(i);
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      const fileName = `Additional_Data_${new Date().getTime()}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);

    try {
      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          // Map MIME type to docx accepted format
          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      const sections = data?.content?.sections;
      const children: (Paragraph | Table)[] = [];

      const pageWidth = convertInchesToTwip(8.5);
      const marginLeft = convertInchesToTwip(0.75);
      const marginRight = convertInchesToTwip(0.75);
      const availableWidth = pageWidth - marginLeft - marginRight;

      // Header
      children.push(
        new Paragraph({
          text: "Additional Data",
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200, before: 200 },
        })
      );

      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Generated on ${currentDateTime}`,
              size: 20,
              color: "666666",
              italics: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 600 },
        })
      );

      const col1Width = availableWidth * 0.3;
      const col2Width = availableWidth * 0.7;

      // Sections
      sections?.forEach((section: any) => {
        children.push(
          new Paragraph({
            text: section.section,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        if (section.details) {
          const tableRows: TableRow[] = [
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: "Field",
                          bold: true,
                          size: 20,
                          color: "FFFFFF",
                        }),
                      ],
                    }),
                  ],
                  shading: { fill: "4472C4" },
                  width: { size: col1Width, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: "Details",
                          bold: true,
                          size: 20,
                          color: "FFFFFF",
                        }),
                      ],
                    }),
                  ],
                  shading: { fill: "4472C4" },
                  width: { size: col2Width, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
              ],
              tableHeader: true,
            }),
          ];

          let rowIdx = 0;
          Object.entries(section.details).forEach(([key, value]) => {
            const label = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l: string) => l.toUpperCase());

            tableRows.push(
              new TableRow({
                children: [
                  new TableCell({
                    children: [
                      new Paragraph({
                        text: label,
                        spacing: { after: 80, before: 80 },
                      }),
                    ],
                    shading: { fill: rowIdx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    width: { size: col1Width, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [
                      new Paragraph({
                        text: formatValue(value),
                        spacing: { after: 80, before: 80 },
                      }),
                    ],
                    shading: { fill: rowIdx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    width: { size: col2Width, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                ],
              })
            );
            rowIdx++;
          });

          const sectionTable = new Table({
            rows: tableRows,
            width: { size: availableWidth, type: WidthType.DXA },
            columnWidths: [col1Width, col2Width],
            layout: TableLayoutType.FIXED,
            borders: {
              top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              insideHorizontal: {
                style: BorderStyle.SINGLE,
                size: 1,
                color: "DDDDDD",
              },
              insideVertical: {
                style: BorderStyle.SINGLE,
                size: 1,
                color: "DDDDDD",
              },
            },
          });

          children.push(sectionTable);
          children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
        }
      });

      // Create footer with logo in right bottom corner
      const footerChildren: Paragraph[] = [];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }

      // Create Document
      const doc = new Document({
        creator: "Additional Data System",
        title: "Additional Data",
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(0.75),
                  left: convertInchesToTwip(0.75),
                  right: convertInchesToTwip(0.75),
                },
                size: {
                  width: convertInchesToTwip(8.5),
                  height: convertInchesToTwip(11),
                  orientation: PageOrientation.PORTRAIT,
                },
              },
            },
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
            children: children,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `Additional_Data_${new Date().getTime()}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Failed to generate Word document. Please try again.");
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading)
    return <div className="h-64 bg-gray-200 rounded animate-pulse" />;

  if (error)
    return (
      <div className="max-w-4xl mx-auto mt-8">
        <div className="bg-gray-50 border border-gray-300 rounded-xl p-6 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Data
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );

  const sections = data?.content?.sections;

  return (
    <div className="bg-white p-6 rounded-2xl shadow border border-gray-300 max-w-4xl mx-auto">
      {/* Header with Download Buttons */}
      <div className="pb-6 border-b border-gray-300 mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl text-gray-700 font-light">Additional Data</h3>

          <div className="flex gap-2">
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  PDF
                </>
              )}
            </button>

            <button
              onClick={downloadWord}
              disabled={downloadingWord}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloadingWord ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  Word
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Sections */}
      {sections?.map((section: any, idx: number) => (
        <div
          key={section.section}
          className={`mb-6 ${
            idx !== sections.length - 1 ? "pb-6 border-b border-gray-300" : ""
          }`}
        >
          <h4 className="text-md font-light text-gray-500 mb-4">
            {section.section}
          </h4>

          {section.details && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full  rounded-lg table-fixed">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 text-sm w-1/3">
                      Field
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 text-sm">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(section.details).map(
                    ([key, value]: [string, any]) => {
                      // Handle array of objects
                      if (Array.isArray(value)) {
                        return (
                          <tr
                            key={key}
                            className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-3 px-4 text-sm font-medium text-gray-800 capitalize align-top">
                              {key.replace(/_/g, " ")}
                            </td>
                            <td className="py-3 px-4">
                              <div className="w-full">
                                <table className="w-full divide-y divide-gray-200 border border-gray-300  table-fixed">
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {value.map((item: any, itemIdx: number) => {
                                      if (typeof item === "object") {
                                        return Object.entries(item).map(
                                          (
                                            [itemKey, itemValue]: [string, any],
                                            entryIdx: number
                                          ) => (
                                            <tr
                                              key={`${itemIdx}-${itemKey}`}
                                              className={
                                                entryIdx % 2 === 0
                                                  ? "bg-white"
                                                  : "bg-gray-50"
                                              }
                                            >
                                              {entryIdx === 0 && (
                                                <td
                                                  rowSpan={
                                                    Object.keys(item).length
                                                  }
                                                  className="px-3 py-2 text-sm font-semibold text-gray-900 border-r border-gray-300 bg-gray-100 align-top w-16"
                                                >
                                                  {itemIdx + 1}
                                                </td>
                                              )}
                                              <td className="px-3 py-2 text-sm font-medium text-gray-600 capitalize border-r border-gray-300 w-2/5">
                                                {itemKey.replace(/_/g, " ")}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-700 break-words">
                                                {itemValue}
                                              </td>
                                            </tr>
                                          )
                                        );
                                      } else {
                                        return (
                                          <tr
                                            key={itemIdx}
                                            className="bg-white"
                                          >
                                            <td
                                              colSpan={3}
                                              className="px-3 py-2 text-sm text-gray-700 border-t border-gray-300"
                                            >
                                              {item}
                                            </td>
                                          </tr>
                                        );
                                      }
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      // Handle status field with special styling
                      if (key === "status") {
                        return (
                          <tr
                            key={key}
                            className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-3 px-4 text-sm font-medium text-gray-800 capitalize">
                              {key.replace(/_/g, " ")}
                            </td>
                            <td className="py-3 px-4">
                              <span className="inline-block bg-gray-50 text-gray-700 text-sm font-medium px-3 py-1 rounded-full border border-gray-300">
                                {value as string}
                              </span>
                            </td>
                          </tr>
                        );
                      }

                      // Handle regular string fields
                      if (typeof value === "string") {
                        return (
                          <tr
                            key={key}
                            className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-3 px-4 text-sm font-medium text-gray-800 capitalize align-top">
                              {key.replace(/_/g, " ")}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-700 leading-relaxed">
                              {value}
                            </td>
                          </tr>
                        );
                      }

                      // Handle object fields (nested data)
                      if (typeof value === "object" && value !== null) {
                        return (
                          <tr
                            key={key}
                            className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-3 px-4 text-sm font-medium text-gray-800 capitalize align-top">
                              {key.replace(/_/g, " ")}
                            </td>
                            <td className="py-3 px-4">
                              <div className="space-y-2">
                                {Object.entries(value).map(
                                  ([subKey, subValue]: [string, any]) => (
                                    <div key={subKey} className="text-sm">
                                      <span className="text-xs font-semibold text-gray-500 capitalize">
                                        {subKey.replace(/_/g, " ")}:{" "}
                                      </span>
                                      <span className="text-gray-700">
                                        {subValue as string}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return null;
                    }
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {data?.citations && data.citations.length > 0 && (
        <div className="text-gray-700 text-sm">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Sources</h4>
          <p className="mb-1">
            {data.citations.map((sourceUrl: string, i: number) => {
              const url = new URL(sourceUrl);
              const display = url.hostname.replace("www.", "");
              return (
                <button
                  key={i}
                  onClick={(e) => handleSourceClick(e, sourceUrl)}
                  className="text-xs text-gray-700 border ml-1 mb-1 border-gray-300 font-medium px-2 py-1 rounded-full hover:bg-gray-50 cursor-pointer"
                >
                  {display}
                </button>
              );
            })}
          </p>
        </div>
      )}

      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};
